import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableRow, TableHeader, TableHead } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AlertTriangle, CheckCircle, XCircle, HelpCircle, TrendingUp, DollarSign, BarChart3, SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

const fmt = (val) => {
  if (val === null || val === undefined) return "$0";
  const n = Number(val);
  return n < 0 ? `-$${Math.abs(n).toLocaleString()}` : `$${n.toLocaleString()}`;
};

const ConfBadge = ({ level }) => {
  const m = {
    high: "bg-emerald-500/15 text-emerald-500 border-emerald-500/25",
    medium: "bg-blue-500/15 text-blue-400 border-blue-500/25",
    "medium-low": "bg-amber-500/15 text-amber-500 border-amber-500/25",
    low: "bg-red-500/15 text-red-400 border-red-500/25",
  };
  return <Badge className={`text-[9px] px-1.5 py-0 ${m[level] || m.low}`}>{level}</Badge>;
};

const ArvMethodLabel = ({ method, compCount }) => {
  const labels = {
    valuation_engine: `${compCount} Adjusted Comps`,
    sold_comps: `${compCount} Sold Comps`,
    zestimate: "Zestimate Only",
    price_estimate: "Price Estimate",
    user_override: "Manual Override",
  };
  return <span className="text-xs text-muted-foreground">{labels[method] || method}</span>;
};

const FixFlipSection = ({ fixFlip, params, onParamsChange }) => {
  const [showEditor, setShowEditor] = useState(false);
  const [localParams, setLocalParams] = useState({
    flip_rehab_scope: params?.flip_rehab_scope || "cosmetic",
    flip_rehab_per_sqft: params?.flip_rehab_per_sqft || 0,
    flip_financing_rate: params?.flip_financing_rate || 12.0,
    flip_financing_points: params?.flip_financing_points || 2.0,
    flip_financing_ltv: params?.flip_financing_ltv || 85.0,
    flip_agent_commission: params?.flip_agent_commission || 5.0,
    flip_monthly_utilities: params?.flip_monthly_utilities || 200,
  });

  if (!fixFlip || !fixFlip.verdict) return null;
  const f = fixFlip;

  const handleApply = () => {
    if (onParamsChange) {
      onParamsChange({ ...params, ...localParams });
    }
  };

  const updateLocal = (key, val) => {
    setLocalParams(prev => ({ ...prev, [key]: val }));
  };

  const scopeOptions = [
    { value: "cosmetic", label: "Cosmetic ($12/sqft)", desc: "Paint, flooring, fixtures" },
    { value: "moderate", label: "Moderate ($25/sqft)", desc: "Kitchen, baths, systems" },
    { value: "heavy", label: "Heavy ($50/sqft)", desc: "Structural, full systems" },
    { value: "gut", label: "Gut ($80/sqft)", desc: "Full gut renovation" },
  ];

  const getVerdictStyle = (v) => {
    if (v.startsWith("Strong")) return { bg: "bg-emerald-500/10 border-emerald-500/30 text-emerald-500", icon: <CheckCircle className="w-5 h-5" /> };
    if (v.startsWith("Possible")) return { bg: "bg-blue-500/10 border-blue-500/30 text-blue-400", icon: <TrendingUp className="w-5 h-5" /> };
    if (v.startsWith("Not")) return { bg: "bg-red-500/10 border-red-500/30 text-red-500", icon: <XCircle className="w-5 h-5" /> };
    return { bg: "bg-amber-500/10 border-amber-500/30 text-amber-500", icon: <AlertTriangle className="w-5 h-5" /> };
  };
  const vs = getVerdictStyle(f.verdict);
  const rb = f.rehab_breakdown || {};

  return (
    <div className="space-y-4" data-testid="fix-flip-section">
      {/* Verdict + Explanation */}
      <div className={`rounded-xl border p-4 ${vs.bg}`} data-testid="flip-verdict">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">{vs.icon}</div>
          <div className="flex-1">
            <p className="text-lg font-bold">{f.verdict}</p>
            {f.verdict_explanation && <p className="text-xs mt-1 opacity-80 leading-relaxed">{f.verdict_explanation}</p>}
          </div>
          <div className="text-right shrink-0">
            <p className="text-2xl font-bold font-mono">{f.deal_score}</p>
            <p className="text-[9px] uppercase tracking-wider opacity-60">/100</p>
          </div>
        </div>
      </div>

      {/* Pricing Guidance — prominent */}
      {f.pricing_guidance && (
        <div className={`rounded-lg border px-4 py-3 flex items-center gap-2 ${f.net_profit > 0 && f.price_vs_mao <= 0 ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-amber-500/5 border-amber-500/20'}`} data-testid="pricing-guidance">
          <DollarSign className={`w-4 h-4 shrink-0 ${f.net_profit > 0 && f.price_vs_mao <= 0 ? 'text-emerald-500' : 'text-amber-500'}`} />
          <p className="text-sm font-medium">{f.pricing_guidance}</p>
        </div>
      )}

      {/* MAO Rule Quick View — 70/75/80% */}
      {f.arv > 0 && f.purchase_price > 0 && (
        <Card className="bg-secondary/20 border-orange-500/20" data-testid="mao-rule-section">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-orange-500 uppercase tracking-wider mb-3">MAO = (ARV &times; %) &minus; Rehab</p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { pct: 70, mao: f.mao_70 || Math.round(f.arv * 0.70 - (f.rehab_total || 0)), label: "Conservative" },
                { pct: 75, mao: f.mao_75 || Math.round(f.arv * 0.75 - (f.rehab_total || 0)), label: "Standard" },
                { pct: 80, mao: f.mao_80 || Math.round(f.arv * 0.80 - (f.rehab_total || 0)), label: "Aggressive" },
              ].map(({ pct, mao, label }) => {
                const passes = f.purchase_price <= mao;
                const diff = f.purchase_price - mao;
                return (
                  <div key={pct} className={`text-center p-3 rounded-lg border ${passes ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5'}`} data-testid={`mao-detail-${pct}`}>
                    <p className="text-[10px] text-muted-foreground font-semibold">{pct}% Rule</p>
                    <p className="text-[9px] text-muted-foreground">{label}</p>
                    <p className={`font-mono text-base font-bold mt-1 ${passes ? 'text-emerald-500' : 'text-red-400'}`}>{fmt(mao)}</p>
                    <p className={`text-[10px] font-bold mt-1 ${passes ? 'text-emerald-500' : 'text-red-400'}`}>
                      {passes ? `PASS (${fmt(Math.abs(diff))} under)` : `FAIL (${fmt(diff)} over)`}
                    </p>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 pt-3 border-t border-border/30 flex items-center justify-between">
              <div>
                <p className="text-[10px] text-muted-foreground">Full MAO (all costs + $35K profit)</p>
                <p className="font-mono text-sm font-bold">{fmt(f.mao)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-muted-foreground">Asking Price</p>
                <p className="font-mono text-sm font-bold">{fmt(f.purchase_price)}</p>
              </div>
              <div className={`px-3 py-1.5 rounded-lg text-xs font-bold ${f.purchase_price <= f.mao ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/10 text-red-400'}`}>
                {f.purchase_price <= f.mao ? `${fmt(Math.abs(f.price_vs_mao))} BELOW` : `${fmt(f.price_vs_mao)} ABOVE`}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Editable Flip Parameters */}
      {onParamsChange && (
        <Card className="bg-secondary/20 border-border" data-testid="flip-params-editor">
          <CardContent className="p-0">
            <button
              onClick={() => setShowEditor(!showEditor)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/40 transition-colors"
              data-testid="flip-params-toggle"
            >
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-orange-500" />
                <span className="text-xs font-semibold">Edit Flip Assumptions</span>
              </div>
              {showEditor ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>
            {showEditor && (
              <div className="px-4 pb-4 space-y-3 border-t border-border/50">
                <div className="pt-3">
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Rehab Scope</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                    {scopeOptions.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => updateLocal("flip_rehab_scope", opt.value)}
                        className={`text-left p-2 rounded-lg border text-xs transition-colors ${localParams.flip_rehab_scope === opt.value ? 'border-orange-500 bg-orange-500/10 text-orange-500' : 'border-border hover:border-border/80'}`}
                        data-testid={`scope-${opt.value}`}
                      >
                        <p className="font-semibold">{opt.label.split(' (')[0]}</p>
                        <p className="text-[9px] text-muted-foreground">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Custom $/sqft</label>
                    <Input
                      type="number" min="0" step="1" placeholder="Auto"
                      value={localParams.flip_rehab_per_sqft || ""}
                      onChange={e => updateLocal("flip_rehab_per_sqft", parseFloat(e.target.value) || 0)}
                      className="h-8 text-xs"
                      data-testid="input-rehab-per-sqft"
                    />
                    <p className="text-[9px] text-muted-foreground mt-0.5">0 = use scope default</p>
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">HM Rate %</label>
                    <Input
                      type="number" min="0" max="25" step="0.5"
                      value={localParams.flip_financing_rate}
                      onChange={e => updateLocal("flip_financing_rate", parseFloat(e.target.value) || 0)}
                      className="h-8 text-xs"
                      data-testid="input-financing-rate"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Points %</label>
                    <Input
                      type="number" min="0" max="10" step="0.5"
                      value={localParams.flip_financing_points}
                      onChange={e => updateLocal("flip_financing_points", parseFloat(e.target.value) || 0)}
                      className="h-8 text-xs"
                      data-testid="input-financing-points"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">LTV %</label>
                    <Input
                      type="number" min="0" max="100" step="5"
                      value={localParams.flip_financing_ltv}
                      onChange={e => updateLocal("flip_financing_ltv", parseFloat(e.target.value) || 0)}
                      className="h-8 text-xs"
                      data-testid="input-ltv"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Agent Commission %</label>
                    <Input
                      type="number" min="0" max="10" step="0.5"
                      value={localParams.flip_agent_commission}
                      onChange={e => updateLocal("flip_agent_commission", parseFloat(e.target.value) || 0)}
                      className="h-8 text-xs"
                      data-testid="input-commission"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Utilities $/mo</label>
                    <Input
                      type="number" min="0" step="25"
                      value={localParams.flip_monthly_utilities}
                      onChange={e => updateLocal("flip_monthly_utilities", parseFloat(e.target.value) || 0)}
                      className="h-8 text-xs"
                      data-testid="input-utilities"
                    />
                  </div>
                </div>

                <Button size="sm" onClick={handleApply} className="w-full bg-orange-600 hover:bg-orange-700 text-white text-xs h-8" data-testid="apply-flip-params">
                  Recalculate Flip
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg bg-secondary/30 border border-border text-center">
          <p className="text-[10px] text-muted-foreground uppercase">Net Profit</p>
          <p className={`font-mono text-lg font-bold ${f.net_profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{fmt(f.net_profit)}</p>
        </div>
        <div className="p-3 rounded-lg bg-secondary/30 border border-border text-center">
          <p className="text-[10px] text-muted-foreground uppercase">Cash ROI</p>
          <p className={`font-mono text-lg font-bold ${f.cash_roi >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{f.cash_roi}%</p>
        </div>
        <div className="p-3 rounded-lg bg-secondary/30 border border-border text-center">
          <p className="text-[10px] text-muted-foreground uppercase">Project Margin</p>
          <p className={`font-mono text-lg font-bold ${f.project_margin >= 0.07 ? 'text-emerald-500' : f.project_margin > 0 ? 'text-amber-500' : 'text-red-500'}`}>{(f.project_margin * 100).toFixed(1)}%</p>
        </div>
        <div className="p-3 rounded-lg bg-secondary/30 border border-border text-center">
          <p className="text-[10px] text-muted-foreground uppercase">MAO (75%)</p>
          <p className="font-mono text-lg font-bold text-foreground">{fmt(f.mao_75 || Math.round(f.arv * 0.75 - (f.rehab_total || 0)) || f.mao)}</p>
          {f.purchase_price <= (f.mao_75 || Math.round(f.arv * 0.75 - (f.rehab_total || 0)) || f.mao) && <p className="text-[9px] text-emerald-500 font-semibold">PASS</p>}
          {f.purchase_price > (f.mao_75 || Math.round(f.arv * 0.75 - (f.rehab_total || 0)) || f.mao) && <p className="text-[9px] text-red-400 font-semibold">FAIL</p>}
        </div>
      </div>

      {/* ARV Source — explicit */}
      <Card className="bg-secondary/30 border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-muted-foreground" />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">ARV Source</p>
            </div>
            <div className="flex items-center gap-2">
              <ArvMethodLabel method={f.arv_method} compCount={f.arv_comp_count} />
              <ConfBadge level={f.arv_confidence} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-[10px] text-muted-foreground">ARV</p>
              <p className="font-mono text-sm font-bold">{fmt(f.arv)}</p>
              {f.arv_low > 0 && f.arv_high > 0 && (
                <p className="text-[9px] text-muted-foreground">{fmt(f.arv_low)} – {fmt(f.arv_high)}</p>
              )}
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">Purchase</p>
              <p className="font-mono text-sm font-bold">{fmt(f.purchase_price)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">Spread</p>
              <p className={`font-mono text-sm font-bold ${f.arv - f.purchase_price > 0 ? 'text-emerald-500' : 'text-red-500'}`}>{fmt(f.arv - f.purchase_price)}</p>
              <p className="text-[9px] text-muted-foreground">{f.purchase_price > 0 ? `${(((f.arv - f.purchase_price) / f.purchase_price) * 100).toFixed(1)}%` : ''}</p>
            </div>
          </div>
          {f.arv_confidence_score > 0 && (
            <div className="mt-2 pt-2 border-t border-border/30">
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-muted-foreground">Confidence</span>
                <span className="font-mono font-bold">{f.arv_confidence_score}/100</span>
              </div>
              <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mt-1">
                <div className={`h-full rounded-full ${f.arv_confidence_score >= 70 ? 'bg-emerald-500' : f.arv_confidence_score >= 45 ? 'bg-blue-500' : 'bg-amber-500'}`} style={{ width: `${f.arv_confidence_score}%` }} />
              </div>
              {f.arv_reason_codes && f.arv_reason_codes.length > 0 && (
                <div className="mt-1.5 space-y-0.5">
                  {f.arv_reason_codes.map((r, i) => (
                    <p key={i} className="text-[9px] text-amber-500 flex items-start gap-1"><AlertTriangle className="w-2.5 h-2.5 mt-0.5 shrink-0" />{r}</p>
                  ))}
                </div>
              )}
            </div>
          )}
          {f.arv_method !== 'sold_comps' && f.arv_method !== 'valuation_engine' && f.arv_method !== 'user_override' && (
            <p className="text-[10px] text-amber-500 mt-2 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> ARV not backed by sold comps — verify with a broker CMA before making an offer.</p>
          )}
        </CardContent>
      </Card>

      {/* Comp Adjustment Table — shows each comp with adjustments */}
      {f.arv_comps && f.arv_comps.length > 0 && (
        <Card className="bg-secondary/30 border-border" data-testid="comp-adjustment-table">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Comp Adjustments ({f.arv_comps.length} comps)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="text-[10px]">
                  <TableHead className="text-[10px] px-2 py-1.5">Comp</TableHead>
                  <TableHead className="text-[10px] px-2 py-1.5 text-right">Sale Price</TableHead>
                  <TableHead className="text-[10px] px-2 py-1.5 text-right">Time</TableHead>
                  <TableHead className="text-[10px] px-2 py-1.5 text-right">Size</TableHead>
                  <TableHead className="text-[10px] px-2 py-1.5 text-right">Bed/Bath</TableHead>
                  <TableHead className="text-[10px] px-2 py-1.5 text-right">Cond.</TableHead>
                  <TableHead className="text-[10px] px-2 py-1.5 text-right font-bold">Adjusted</TableHead>
                  <TableHead className="text-[10px] px-2 py-1.5 text-right">Sim.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {f.arv_comps.map((comp, i) => {
                  const adjPct = comp.adjustment_pct || 0;
                  return (
                    <TableRow key={i} className={`text-[10px] ${comp.is_outlier ? 'opacity-40 line-through' : ''}`}>
                      <TableCell className="px-2 py-1.5 max-w-[140px]">
                        <p className="font-medium text-foreground truncate">{comp.address || `Comp ${i + 1}`}</p>
                        <p className="text-[8px] text-muted-foreground">
                          {comp.sqft ? `${comp.sqft.toLocaleString()} sqft` : ''}{comp.beds ? ` | ${comp.beds}bd/${comp.baths}ba` : ''}{comp.days_ago ? ` | ${comp.days_ago}d ago` : ''}{comp.distance_miles ? ` | ${comp.distance_miles}mi` : ''}
                        </p>
                        {comp.exclusion_reason && <p className="text-[8px] text-amber-500">{comp.exclusion_reason}</p>}
                      </TableCell>
                      <TableCell className="font-mono px-2 py-1.5 text-right">{fmt(comp.sale_price)}</TableCell>
                      <TableCell className={`font-mono px-2 py-1.5 text-right ${comp.time_adjustment > 0 ? 'text-emerald-500' : comp.time_adjustment < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                        {comp.time_adjustment !== 0 ? `${comp.time_adjustment > 0 ? '+' : ''}${fmt(comp.time_adjustment)}` : '—'}
                      </TableCell>
                      <TableCell className={`font-mono px-2 py-1.5 text-right ${comp.size_adjustment > 0 ? 'text-emerald-500' : comp.size_adjustment < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                        {comp.size_adjustment !== 0 ? `${comp.size_adjustment > 0 ? '+' : ''}${fmt(comp.size_adjustment)}` : '—'}
                      </TableCell>
                      <TableCell className={`font-mono px-2 py-1.5 text-right ${(comp.bed_adjustment + comp.bath_adjustment) > 0 ? 'text-emerald-500' : (comp.bed_adjustment + comp.bath_adjustment) < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                        {(comp.bed_adjustment + comp.bath_adjustment) !== 0 ? `${(comp.bed_adjustment + comp.bath_adjustment) > 0 ? '+' : ''}${fmt(comp.bed_adjustment + comp.bath_adjustment)}` : '—'}
                      </TableCell>
                      <TableCell className={`font-mono px-2 py-1.5 text-right ${comp.condition_adjustment > 0 ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                        {comp.condition_adjustment !== 0 ? `+${fmt(comp.condition_adjustment)}` : '—'}
                      </TableCell>
                      <TableCell className="font-mono px-2 py-1.5 text-right font-bold">{fmt(comp.adjusted_value)}</TableCell>
                      <TableCell className="px-2 py-1.5 text-right">
                        <span className={`font-mono font-semibold ${comp.similarity_score >= 75 ? 'text-emerald-500' : comp.similarity_score >= 55 ? 'text-blue-400' : 'text-amber-500'}`}>
                          {comp.similarity_score}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="px-4 py-2 border-t border-border/30 flex items-center justify-between text-[10px] text-muted-foreground">
              <span>Weighted Mean: <span className="font-mono font-bold text-foreground">{fmt(f.arv_weighted_mean || f.arv_median_adjusted)}</span></span>
              <span>Median: <span className="font-mono font-bold text-foreground">{fmt(f.arv_median_adjusted)}</span></span>
              <span>Final ARV (70/30 blend): <span className="font-mono font-bold text-foreground">{fmt(f.arv)}</span></span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Full Cost Breakdown */}
      <Card className="bg-secondary/30 border-border">
        <CardHeader className="pb-2 pt-3 px-4"><CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Full Cost Breakdown</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableBody>
              <TableRow className="bg-emerald-500/5">
                <TableCell className="text-emerald-500 text-sm font-semibold">Sale Price (ARV)</TableCell>
                <TableCell className="font-mono text-right text-emerald-500 text-sm font-bold">{fmt(f.arv)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-muted-foreground text-sm">Commission ({f.agent_commission_pct}%)</TableCell>
                <TableCell className="font-mono text-right text-red-400 text-sm">-{fmt(f.selling_commission)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-muted-foreground text-sm">Seller Closing + Staging</TableCell>
                <TableCell className="font-mono text-right text-red-400 text-sm">-{fmt(f.selling_closing + f.selling_staging)}</TableCell>
              </TableRow>
              <TableRow className="bg-secondary/50">
                <TableCell className="text-foreground text-sm font-semibold">Net Sale Proceeds</TableCell>
                <TableCell className="font-mono text-right text-foreground text-sm font-bold">{fmt(f.arv - f.selling_costs)}</TableCell>
              </TableRow>

              {/* Costs */}
              <TableRow>
                <TableCell className="text-muted-foreground text-sm">Purchase Price</TableCell>
                <TableCell className="font-mono text-right text-red-400 text-sm">-{fmt(f.purchase_price)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-muted-foreground text-sm">Acquisition Costs (closing + inspection)</TableCell>
                <TableCell className="font-mono text-right text-red-400 text-sm">-{fmt(f.acquisition_costs)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-muted-foreground text-sm">Rehab ({f.rehab_tier}, ${f.rehab_per_sqft}/sqft) <ConfBadge level={f.rehab_confidence} /></TableCell>
                <TableCell className="font-mono text-right text-red-400 text-sm">-{fmt(f.rehab_base)}</TableCell>
              </TableRow>
              {f.rehab_contingency > 0 && (
                <TableRow>
                  <TableCell className="text-muted-foreground text-sm pl-6">Contingency ({f.rehab_base > 0 ? Math.round(f.rehab_contingency / f.rehab_base * 100) : 0}%)</TableCell>
                  <TableCell className="font-mono text-right text-red-400 text-sm">-{fmt(f.rehab_contingency)}</TableCell>
                </TableRow>
              )}
              {!f.is_cash_purchase && (
                <TableRow>
                  <TableCell className="text-muted-foreground text-sm">Financing Fees (points {f.financing_points_pct}%)</TableCell>
                  <TableCell className="font-mono text-right text-red-400 text-sm">-{fmt(f.financing_costs)}</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell className="text-muted-foreground text-sm">Holding Costs ({f.holding_months}mo x ${f.holding_monthly?.toLocaleString()}/mo)</TableCell>
                <TableCell className="font-mono text-right text-red-400 text-sm">-{fmt(f.holding_costs)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-muted-foreground text-[10px] pl-6 text-zinc-500">
                  Interest ${f.financing_monthly_interest?.toLocaleString()} + Tax ${f.holding_taxes_monthly?.toLocaleString()} + Ins ${f.holding_insurance_monthly?.toLocaleString()} + Utils ${f.holding_utilities_monthly?.toLocaleString()} + Misc ${f.holding_misc_monthly?.toLocaleString() || 250}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>

              <TableRow className="bg-secondary/60 border-t">
                <TableCell className="font-semibold text-sm">Total Project Cost</TableCell>
                <TableCell className="font-mono text-right font-bold text-sm">{fmt(f.total_project_cost)}</TableCell>
              </TableRow>
              <TableRow className={`border-t-2 ${f.net_profit >= 0 ? 'bg-emerald-500/5' : 'bg-red-500/5'}`}>
                <TableCell className="font-bold text-sm">Net Profit</TableCell>
                <TableCell className={`font-mono text-right font-bold text-sm ${f.net_profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{fmt(f.net_profit)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-muted-foreground text-sm">Project Margin (Profit / ARV)</TableCell>
                <TableCell className={`font-mono text-right text-sm font-semibold ${f.project_margin >= 0.07 ? 'text-emerald-500' : f.project_margin > 0 ? 'text-amber-500' : 'text-red-500'}`}>{(f.project_margin * 100).toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-muted-foreground text-sm">Maximum Allowable Offer</TableCell>
                <TableCell className="font-mono text-right text-sm font-semibold">{fmt(f.mao)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Stress Test */}
      <Card className="bg-secondary/30 border-border">
        <CardHeader className="pb-2 pt-3 px-4"><CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Stress Test — Scenarios</CardTitle></CardHeader>
        <CardContent className="px-4 pb-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Scenario</TableHead>
                <TableHead className="text-xs text-right">ARV</TableHead>
                <TableHead className="text-xs text-right">Rehab</TableHead>
                <TableHead className="text-xs text-right">Hold</TableHead>
                <TableHead className="text-xs text-right">Profit</TableHead>
                <TableHead className="text-xs text-right">Margin</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[f.best_case, f.expected_case, f.conservative_case].filter(Boolean).map((s) => (
                <TableRow key={s.label}>
                  <TableCell className="text-sm font-medium">{s.label}</TableCell>
                  <TableCell className="font-mono text-right text-xs">{fmt(s.arv)}</TableCell>
                  <TableCell className="font-mono text-right text-xs">{fmt(s.rehab)}</TableCell>
                  <TableCell className="font-mono text-right text-xs">{s.hold_months}mo</TableCell>
                  <TableCell className={`font-mono text-right text-xs font-bold ${s.net_profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{fmt(s.net_profit)}</TableCell>
                  <TableCell className={`font-mono text-right text-xs ${s.project_margin >= 0.07 ? 'text-emerald-500' : s.project_margin > 0 ? 'text-amber-500' : 'text-red-500'}`}>{(s.project_margin * 100).toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {f.conservative_case?.net_profit < 0 && (
            <p className="text-[10px] text-red-400 mt-2 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> Conservative scenario shows a loss — profit is highly assumption-sensitive.</p>
          )}
        </CardContent>
      </Card>

      {/* Risk Flags */}
      {f.risk_flags?.length > 0 && (
        <Card className="bg-red-500/5 border-red-500/20">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2">Risk Flags</p>
            <div className="space-y-1.5">
              {f.risk_flags.map((flag, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase shrink-0 ${flag.severity === 'high' ? 'bg-red-500/20 text-red-400' : flag.severity === 'medium' ? 'bg-amber-500/20 text-amber-500' : 'bg-zinc-500/20 text-zinc-400'}`}>{flag.severity}</span>
                  <p className="text-xs text-muted-foreground">{flag.message}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* What Would Make This Work */}
      {f.action_guidance?.length > 0 && (
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">What Would Make This Work</p>
            <ul className="space-y-1">
              {f.action_guidance.map((g, i) => (
                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <span className="text-blue-400 mt-0.5 shrink-0">→</span> {g}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Sold Comps */}
      {f.arv_comps?.length > 0 && (
        <Card className="bg-secondary/30 border-border">
          <CardHeader className="pb-2 pt-3 px-4"><CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sold Comps ({f.arv_comp_count} used for ARV)</CardTitle></CardHeader>
          <CardContent className="px-4 pb-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Address</TableHead>
                  <TableHead className="text-xs text-right">Sold</TableHead>
                  <TableHead className="text-xs text-right">Sqft</TableHead>
                  <TableHead className="text-xs text-right">Bd/Ba</TableHead>
                  <TableHead className="text-xs text-right">Adjusted</TableHead>
                  <TableHead className="text-xs text-right">Weight</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {f.arv_comps.map((c, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs text-muted-foreground truncate max-w-[160px]">{c.address}</TableCell>
                    <TableCell className="font-mono text-right text-xs">{fmt(c.sale_price)}</TableCell>
                    <TableCell className="font-mono text-right text-xs">{c.sqft?.toLocaleString()}</TableCell>
                    <TableCell className="font-mono text-right text-xs">{c.beds}/{c.baths}</TableCell>
                    <TableCell className="font-mono text-right text-xs font-semibold">{fmt(c.adjusted_price)}</TableCell>
                    <TableCell className="font-mono text-right text-xs text-muted-foreground">{(c.weight * 100).toFixed(0)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Rehab Breakdown */}
      {Object.keys(rb).length > 0 && (
        <Card className="bg-secondary/30 border-border">
          <CardHeader className="pb-2 pt-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Rehab Breakdown</CardTitle>
              <ConfBadge level={f.rehab_confidence} />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableBody>
                {Object.entries(rb).map(([key, val]) => (
                  <TableRow key={key}>
                    <TableCell className="text-muted-foreground text-sm capitalize">{key.replace(/_/g, ' ')}</TableCell>
                    <TableCell className="font-mono text-right text-sm">{fmt(val)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-secondary/50 border-t">
                  <TableCell className="text-muted-foreground text-sm">Contingency</TableCell>
                  <TableCell className="font-mono text-right text-sm">{fmt(f.rehab_contingency)}</TableCell>
                </TableRow>
                <TableRow className="bg-secondary/50 border-t-2">
                  <TableCell className="font-bold text-sm">Total Rehab</TableCell>
                  <TableCell className="font-mono text-right font-bold text-sm">{fmt(f.rehab_total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FixFlipSection;
